import { useEffect, useRef } from 'react';
import { getCachedAudio } from './musicCache';
import { getMobileGentleMusicMode } from './musicMobileHeuristics';
import { useMusic as useMusicContext } from '../music/MusicContext';
import {
  getCurrentPlayingAudio,
  setCurrentPlayingAudio,
  getCurrentFadeOutPromise,
  setCurrentFadeOutPromise,
  acquireTrackStartLock,
  stopAllAudio,
  registerAudioElement,
  unregisterAudioElement,
  incrementTrackRetain,
  decrementTrackRetain,
  scheduleTrackStopIfUnretained
} from './musicState';

/**
 * Custom hook for managing background music with fade in/out effects
 * Uses cached Audio objects for better performance
 * @param {string} trackPath - Path to the music file (e.g., '/music/track.mp3')
 * @param {number} startTime - Optional start time in seconds (default: 0)
 * @param {number} fadeDuration - Fade in/out duration in milliseconds (default: 2000)
 * @param {boolean} loop - When false, play the track once (victory/defeat stingers)
 */
export function useMusic(trackPath, startTime = 0, fadeDuration = 2000, loop = true) {
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const userInteractionHandlerRef = useRef(null);
  const isPlayingRef = useRef(false);
  const healthCheckIntervalRef = useRef(null);
  const retryCountRef = useRef(0);
  const lastRetryTimeRef = useRef(0);
  const trackEndHandlerRef = useRef(null);
  const lastPlayOkAtRef = useRef(0);
  const healthMissStreakRef = useRef(0);
  const gentleMobile = getMobileGentleMusicMode();
  const { musicEnabled, musicVolume, masterVolume } = useMusicContext();
  const effectiveMusicVolume = Math.min(1, musicVolume * masterVolume);

  useEffect(() => {
    if (!trackPath) return;

    incrementTrackRetain(trackPath);
    
    // If music is disabled, stop any playing audio
    if (!musicEnabled) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.volume = 0;
        audioRef.current.currentTime = 0;
      }
      return () => {
        decrementTrackRetain(trackPath);
      };
    }

    // Get cached audio element (reuses existing if available)
    const audio = getCachedAudio(trackPath);

    // Do not call audio.load() here: it shares the cache with preloadTrack/useMusic and will
    // reset the decoder mid-playback on Android (choppy / interrupted music). play() buffers enough.

    lastPlayOkAtRef.current = 0;
    healthMissStreakRef.current = 0;

    // Register this audio element for tracking
    registerAudioElement(audio);

    const continuingSameTrack =
      getCurrentPlayingAudio() === audio && !audio.paused && audio.currentTime > 0;

    audio.loop = loop;

    if (!continuingSameTrack) {
      audio.volume = 0; // Start at 0 for fade in
      if (startTime > 0) {
        audio.currentTime = startTime;
      }
    }

    audioRef.current = audio;
    let fadeIntervalId = null;
    let releaseLock = null;

    // Fade out function that returns a promise
    const fadeOutAudio = (audioToFade, fadeDurationMs) => {
      return new Promise((resolve) => {
        if (!audioToFade || audioToFade.paused || audioToFade.volume === 0) {
          // Already stopped or at 0 volume, resolve immediately
          resolve();
          return;
        }

        const steps = 50;
        const currentVolume = audioToFade.volume;
        const volumeStep = currentVolume / steps;
        const stepDuration = fadeDurationMs / steps;
        let currentStep = 0;

        const fadeOutInterval = setInterval(() => {
          currentStep++;
          const newVolume = Math.max(currentVolume - (volumeStep * currentStep), 0);
          audioToFade.volume = newVolume;

          if (currentStep >= steps || newVolume <= 0) {
            clearInterval(fadeOutInterval);
            audioToFade.pause();
            audioToFade.currentTime = 0;
            audioToFade.volume = 0;
            resolve();
          }
        }, stepDuration);
      });
    };

    // Fade in (must run after every successful play(); isPlayingRef is reset before each call)
    const fadeIn = () => {
      const targetVolume = effectiveMusicVolume;
      const steps = 50;
      const volumeStep = targetVolume / steps;
      const stepDuration = fadeDuration / steps;
      let currentStep = 0;

      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }

      isPlayingRef.current = true;

      fadeIntervalId = setInterval(() => {
        currentStep++;
        const newVolume = Math.min(volumeStep * currentStep, targetVolume);
        audio.volume = newVolume;

        if (currentStep >= steps) {
          clearInterval(fadeIntervalId);
          fadeIntervalId = null;
          fadeIntervalRef.current = null;
          audio.volume = targetVolume;
        }
      }, stepDuration);

      fadeIntervalRef.current = fadeIntervalId;
    };

    const isSameTrackAlreadyPlaying = () => {
      const current = getCurrentPlayingAudio();
      return current === audio && !audio.paused && audio.currentTime > 0;
    };

    // Start the new track after waiting for any ongoing fade-out
    const startNewTrack = async () => {
      if (isSameTrackAlreadyPlaying()) {
        setCurrentPlayingAudio(audio);
        return;
      }

      // CRITICAL: Acquire lock to prevent concurrent track starts
      releaseLock = await acquireTrackStartLock();
      
      try {
        if (isSameTrackAlreadyPlaying()) {
          setCurrentPlayingAudio(audio);
          return;
        }

        // Double-check: Stop ALL audio as a safety measure
        stopAllAudio();
        
        // Wait a tiny bit to ensure all stops are processed
        await new Promise(resolve => setTimeout(resolve, 10));

        // If there's a currently playing audio that's different from the new one, fade it out
        const currentAudio = getCurrentPlayingAudio();
        if (currentAudio && currentAudio !== audio && !currentAudio.paused) {
          const fadeOutPromise = fadeOutAudio(currentAudio, fadeDuration);
          setCurrentFadeOutPromise(fadeOutPromise);
          await fadeOutPromise;
        }

        // Now set the new audio as current and start it
        setCurrentPlayingAudio(audio);
      } catch (error) {
        // Release lock on error
        if (releaseLock) {
          releaseLock();
          releaseLock = null;
        }
        throw error;
      }
    };

    // Start playing function with retry logic
    const startPlaying = async (isRetry = false) => {
      // Same track still playing (e.g. hub menu → hub menu): keep position, no fade restart
      if (!isRetry && isSameTrackAlreadyPlaying()) {
        setCurrentPlayingAudio(audio);
        isPlayingRef.current = true;
        if (audio.volume < effectiveMusicVolume * 0.9) {
          fadeIn();
        } else {
          audio.volume = effectiveMusicVolume;
        }
        return;
      }

      // Don't start if already playing (unless it's a retry)
      if (!isRetry && isPlayingRef.current && !audio.paused && getCurrentPlayingAudio() === audio) {
        return;
      }
      
      try {
        // Wait for fade-out and start new track (acquires lock)
        await startNewTrack();

        if (isSameTrackAlreadyPlaying()) {
          isPlayingRef.current = true;
          if (audio.volume < effectiveMusicVolume * 0.9) {
            fadeIn();
          } else {
            audio.volume = effectiveMusicVolume;
          }
          if (releaseLock) {
            releaseLock();
            releaseLock = null;
          }
          return;
        }

        // CRITICAL: Verify no other audio is playing before starting
        const currentAudio = getCurrentPlayingAudio();
        if (currentAudio && currentAudio !== audio && !currentAudio.paused) {
          // Emergency stop if something else is playing
          stopAllAudio();
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Reset start time if specified (in case audio was paused)
        if (startTime > 0 && audio.currentTime < startTime) {
          audio.currentTime = startTime;
        }
        
        // Ensure audio is stopped before playing
        if (!audio.paused) {
          audio.pause();
        }
        audio.volume = 0;
        
        await audio.play();
        lastPlayOkAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();

        // Do not attach `ended` when loop=true: Android WebView often fires `ended` spuriously with
        // loop enabled; the handler sought to 0 and replayed → ~2s snippets and scratching.
        if (trackEndHandlerRef.current) {
          audio.removeEventListener('ended', trackEndHandlerRef.current);
          trackEndHandlerRef.current = null;
        }

        isPlayingRef.current = false;
        fadeIn();

        // Reset retry count on success
        retryCountRef.current = 0;
        lastRetryTimeRef.current = 0;
        
        // Release lock after successfully starting
        if (releaseLock) {
          releaseLock();
          releaseLock = null;
        }
        
        // Remove user interaction listener if it was set up
        if (userInteractionHandlerRef.current) {
          document.removeEventListener('click', userInteractionHandlerRef.current);
          document.removeEventListener('touchstart', userInteractionHandlerRef.current);
          document.removeEventListener('keydown', userInteractionHandlerRef.current);
          userInteractionHandlerRef.current = null;
        }
      } catch (error) {
        // Release lock on error
        if (releaseLock) {
          releaseLock();
          releaseLock = null;
        }
        
        // Autoplay was blocked - set up user interaction listener
        if (error.name === 'NotAllowedError' || error.name === 'NotSupportedError') {
          // Only set up listener if we don't already have one
          if (!userInteractionHandlerRef.current) {
            const handleUserInteraction = async () => {
              try {
                await startNewTrack();
                
                // CRITICAL: Verify no other audio is playing
                const currentAudio = getCurrentPlayingAudio();
                if (currentAudio && currentAudio !== audio && !currentAudio.paused) {
                  stopAllAudio();
                  await new Promise(resolve => setTimeout(resolve, 50));
                }
                
                if (startTime > 0 && audio.currentTime < startTime) {
                  audio.currentTime = startTime;
                }
                
                if (!audio.paused) {
                  audio.pause();
                }
                audio.volume = 0;
                
                await audio.play();
                lastPlayOkAtRef.current =
                  typeof performance !== 'undefined' ? performance.now() : Date.now();

                if (fadeIntervalRef.current) {
                  clearInterval(fadeIntervalRef.current);
                  fadeIntervalRef.current = null;
                }
                isPlayingRef.current = false;
                fadeIn();

                // Reset retry count on success
                retryCountRef.current = 0;
                lastRetryTimeRef.current = 0;

                if (releaseLock) {
                  releaseLock();
                  releaseLock = null;
                }
              } catch (err) {
                console.error('Error playing music after user interaction:', err);
                scheduleRetry();
                if (releaseLock) {
                  releaseLock();
                  releaseLock = null;
                }
              }
            };
            
            userInteractionHandlerRef.current = handleUserInteraction;
            document.addEventListener('click', handleUserInteraction, { once: true });
            document.addEventListener('touchstart', handleUserInteraction, { once: true });
            document.addEventListener('keydown', handleUserInteraction, { once: true });
          }
        } else {
          console.error('Error playing music:', error);
          scheduleRetry();
        }
      }
    };

    // Retry logic with exponential backoff
    const scheduleRetry = () => {
      const now = Date.now();
      const timeSinceLastRetry = now - lastRetryTimeRef.current;
      
      // Exponential backoff: 1s, 2s, 4s, 8s, max 10s
      const maxRetries = 5;
      if (retryCountRef.current >= maxRetries) {
        console.warn('Music retry limit reached. Giving up.');
        return;
      }
      
      const backoffDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 10000);
      
      // Don't retry too frequently
      if (timeSinceLastRetry < backoffDelay) {
        return;
      }
      
      retryCountRef.current++;
      lastRetryTimeRef.current = now;
      
      console.log(`Retrying music playback (attempt ${retryCountRef.current}/${maxRetries})...`);
      
      setTimeout(() => {
        // Only retry if music is still enabled and we should be playing
        if (musicEnabled && trackPath && getCurrentPlayingAudio() !== audio) {
          startPlaying(true).catch(err => {
            console.error('Retry failed:', err);
            if (retryCountRef.current < maxRetries) {
              scheduleRetry();
            }
          });
        }
      }, backoffDelay);
    };

    // Health check: verify music is actually playing when it should be.
    // On mobile, frequent checks + full startPlaying() cause audible gaps (stopAllAudio + replay).
    const startHealthCheck = () => {
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }

      // Mobile: avoid periodic play()/recovery — it causes glitches; native loop + no `ended` handler is enough.
      const intervalMs = gentleMobile ? 60000 : 4000;
      const startupGraceMs = gentleMobile ? Math.max(fadeDuration + 2000, 4000) : fadeDuration + 800;
      const sinceOk = () => {
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        if (lastPlayOkAtRef.current <= 0) {
          return 0;
        }
        return now - lastPlayOkAtRef.current;
      };

      healthCheckIntervalRef.current = setInterval(() => {
        if (!musicEnabled || !trackPath) {
          return;
        }

        const shouldBePlaying = getCurrentPlayingAudio() === audio;
        if (!shouldBePlaying) {
          healthMissStreakRef.current = 0;
          return;
        }

        if (sinceOk() < startupGraceMs) {
          healthMissStreakRef.current = 0;
          return;
        }

        // During fade-in, volume can be very small; still "playing"
        const volOk = audio.volume > 0.008 || effectiveMusicVolume <= 0.01;
        const isActuallyPlaying =
          audio && !audio.paused && (volOk || audio.readyState >= 3);

        // Never manually "fix" looped tracks: bogus/late duration metadata triggers false
        // end detection → seek/play loops ~2s and scratches. Rely on native loop only.

        if (!isActuallyPlaying) {
          if (gentleMobile) {
            healthMissStreakRef.current = 0;
            return;
          }
          healthMissStreakRef.current += 1;
          // Require 2 consecutive misses to avoid stopAllAudio+replay on brief buffer/fade glitches
          if (healthMissStreakRef.current < 2) {
            return;
          }
          healthMissStreakRef.current = 0;

          console.warn("Music health check: Music should be playing but isn't. Attempting light recovery...");

          retryCountRef.current = 0;
          lastRetryTimeRef.current = 0;

          startPlaying(true).catch((err) => {
            console.error('Health check recovery failed:', err);
            scheduleRetry();
          });
        } else {
          healthMissStreakRef.current = 0;
        }
      }, intervalMs);
    };

    // Try to start playing
    startPlaying();
    
    // One-shot tracks (victory/defeat) must not be restarted by health-check recovery.
    if (loop) {
      startHealthCheck();
    }

    // Cleanup function - fade out and stop
    return () => {
      decrementTrackRetain(trackPath);

      // Clear health check
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
      
      // Remove user interaction listener if it exists
      if (userInteractionHandlerRef.current) {
        document.removeEventListener('click', userInteractionHandlerRef.current);
        document.removeEventListener('touchstart', userInteractionHandlerRef.current);
        document.removeEventListener('keydown', userInteractionHandlerRef.current);
        userInteractionHandlerRef.current = null;
      }
      
      // Clear any active fade in interval
      if (fadeIntervalRef.current) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
      if (fadeIntervalId) {
        clearInterval(fadeIntervalId);
        fadeIntervalId = null;
      }

      // MOBILE FIX: Remove ended event listener
      if (audio && trackEndHandlerRef.current) {
        audio.removeEventListener('ended', trackEndHandlerRef.current);
        trackEndHandlerRef.current = null;
      }

      isPlayingRef.current = false;
      retryCountRef.current = 0;
      lastRetryTimeRef.current = 0;
      
      // Release lock if held
      if (releaseLock) {
        releaseLock();
        releaseLock = null;
      }
      
      // Unregister audio element
      if (audio) {
        unregisterAudioElement(audio);
      }
      
      const runStopCleanup = () => {
        // Fade out this audio if it's currently playing
        if (audio && getCurrentPlayingAudio() === audio) {
          const fadeOutPromise = fadeOutAudio(audio, fadeDuration);
          setCurrentFadeOutPromise(fadeOutPromise);
          fadeOutPromise.then(() => {
            if (getCurrentPlayingAudio() === audio) {
              setCurrentPlayingAudio(null);
            }
          }).catch(() => {
            if (getCurrentPlayingAudio() === audio) {
              setCurrentPlayingAudio(null);
            }
          });
        } else if (audio && !audio.paused) {
          try {
            audio.pause();
            audio.currentTime = 0;
            audio.volume = 0;
          } catch (e) {
            console.warn('Error stopping untracked audio:', e);
          }
        }
      };

      // Delay stop so the next screen can retain the same track without a gap/restart
      scheduleTrackStopIfUnretained(trackPath, runStopCleanup);
    };
  }, [trackPath, startTime, fadeDuration, musicEnabled, gentleMobile, loop]);

  // Update volume when music or master volume changes (for already playing audio)
  useEffect(() => {
    if (!audioRef.current || !musicEnabled) return;
    
    const audio = audioRef.current;
    // Only update if this audio is currently playing
    if (getCurrentPlayingAudio() === audio && !audio.paused) {
      // Smoothly transition to new volume
      const currentVolume = audio.volume;
      const targetVolume = effectiveMusicVolume;
      const steps = 20;
      const volumeDiff = targetVolume - currentVolume;
      const volumeStep = volumeDiff / steps;
      let currentStep = 0;
      
      const volumeInterval = setInterval(() => {
        currentStep++;
        const newVolume = currentVolume + (volumeStep * currentStep);
        audio.volume = Math.max(0, Math.min(1, newVolume));
        
        if (currentStep >= steps) {
          clearInterval(volumeInterval);
          audio.volume = targetVolume;
        }
      }, 20);
      
      return () => clearInterval(volumeInterval);
    } else if (getCurrentPlayingAudio() === audio) {
      // If it's the current audio but paused, just set the volume directly
      audio.volume = effectiveMusicVolume;
    }
  }, [musicVolume, masterVolume, musicEnabled, effectiveMusicVolume]);

  return audioRef;
}

